import { type CSSProperties } from 'react';
import { useLayoutData } from './useLayoutContext';
import { type EmbeddedAppearance } from '../utils/embeddedAppearance';

type ColorKey = Exclude<
  keyof EmbeddedAppearance,
  'radius' | 'buttonRadius' | 'borderWidth'
>;

/**
 * The variable each colour sets: charlie's shadcn names, plus the brand
 * colour. Typed against the appearance, so a colour added to the schema
 * without a variable here fails typecheck instead of being silently ignored.
 */
const COLOR_VARIABLES = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  border: '--border',
  destructive: '--destructive',
  brandPrimary: '--brand-primary',
} as const satisfies Record<ColorKey, `--${string}`>;

/** The variables charlie's classes and the embedded screens read. */
function tokens(appearance: EmbeddedAppearance) {
  const colors = Object.fromEntries(
    (Object.keys(COLOR_VARIABLES) as ColorKey[]).map((key) => [
      COLOR_VARIABLES[key],
      appearance[key],
    ]),
  );
  return {
    ...colors,
    // The outline button's label (charlie's outline variant); nothing else
    // in the embedded screens uses it
    '--semi-dark-grey': appearance.accentForeground,
    '--radius': `${appearance.radius}px`,
    '--button-radius': `${appearance.buttonRadius}px`,
  };
}

/**
 * The flow brand's appearance as CSS custom properties, for the root of the
 * card or the drawer's sheet, so only Ronan's own surface changes and the
 * drawer's backdrop keeps its look. Undefined without an appearance: the
 * shell then keeps the default look exactly.
 *
 * The outer corners follow the radius: the card's is radius × 1.6 and the
 * sheet's top radius × 2 (16px and 20px at the default 10). Border width
 * belongs to the card; the sheet has no border.
 */
export function useEmbeddedAppearance(
  shell: 'card' | 'drawer',
): CSSProperties | undefined {
  const appearance = useLayoutData().interactionConfig?.embeddedAppearance;
  if (!appearance) return undefined;

  if (shell === 'card') {
    return {
      ...tokens(appearance),
      '--embedded-radius': `${appearance.radius * 1.6}px`,
      '--embedded-border': `${appearance.borderWidth}px`,
    } as CSSProperties;
  }
  return {
    ...tokens(appearance),
    '--embedded-sheet-radius': `${appearance.radius * 2}px`,
  } as CSSProperties;
}
